import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { CheckCircle2, ArrowRight, PhoneCall, MessageSquare, Clock, CalendarClock, Mail } from "lucide-react";
import { copyForProduct } from "@/lib/thank-you-content";
import { trackEvent } from "@/lib/analytics";
import { loadOfferAttribution } from "@/lib/offer-attribution";
import { trackThankYouCta } from "@/lib/tracking";
import { StrReportPanel } from "@/components/site/StrReportPanel";
import { PropertySnapshotCard } from "@/components/site/PropertySnapshotCard";
import { loadPropertySnapshot, hasSnapshotData, type PropertySnapshot } from "@/lib/property-snapshot";
import { PersonalizedResultsPanel } from "@/components/site/PersonalizedResults";
import { PreQualResultCard } from "@/components/site/PreQualResultCard";
import { loadPersonalizedResults, type StoredResults } from "@/lib/results-store";
import { buildHead } from "@/lib/seo-head";
import { applyDynamicMeta, resultsMeta } from "@/lib/dynamic-meta";

import { DocumentChecklistDownload } from "@/components/site/DocumentChecklistDownload";
import { PRODUCTS } from "@/lib/pre-approval-products";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { telHref, getApplicationHref, getBookingHref, getCtaLinkProps } from "@/lib/site-settings";


export const Route = createFileRoute("/apply/thanks/$product")({
  head: ({ params }) => {
    const c = copyForProduct(params.product);
    const label = (PRODUCTS as Record<string, { label?: string }>)[params.product]?.label ?? "loan";
    const url = `/apply/thanks/${params.product}`;
    return buildHead({
      title: `Your ${label} Results | Simply Approved Mortgages`,
      description: `Your personalized ${label} scenario summary — estimated DSCR, max loan, down payment, monthly payment, cash flow and program fit. Illustrative only.`,
      url,
      keywords: `${label.toLowerCase()} results, dscr calculator results, investor loan scenario summary, business purpose loan estimate`,
      noindex: true,
      service: { name: label },
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Apply", url: "/apply" },
        { name: `${label} Results`, url },
      ],
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: `Your ${label} Results`,
          description: c.subhead,
          url,
          isAccessibleForFree: true,
        },
      ],
    });
  },
  component: ThankYouPage,
});


function ThankYouPage() {
  const { product } = Route.useParams();
  const search = Route.useSearch() as { leadId?: string };
  const copy = copyForProduct(product);
  const ctaClick = (
    cta: "schedule_callback" | "complete_application",
    location: string,
    destination?: string,
  ) =>
    trackThankYouCta({
      cta,
      location,
      product,
      productLabel: (PRODUCTS as Record<string, { label?: string }>)[product]?.label,
      leadId: search.leadId ?? null,
      destination,
    });
  const settings = useSiteSettings();
  // Property snapshot (AVM + property details + rent) saved by the lead form.
  const [snapshot, setSnapshot] = useState<PropertySnapshot | null>(null);
  // Personalized results (DSCR, max loan, cash flow, fit) saved on submit.
  const [stored, setStored] = useState<StoredResults | null>(null);
  // Tracks that the sessionStorage read has completed so the conversion event
  // fires exactly once, with state / city / goal attached when available.
  const [storedLoaded, setStoredLoaded] = useState(false);
  const conversionFired = useRef(false);
  useEffect(() => {
    setSnapshot(loadPropertySnapshot(search.leadId));
    setStored(loadPersonalizedResults(search.leadId));
    setStoredLoaded(true);
  }, [search.leadId]);
  // Refine title / Open Graph once the computed DSCR figures are available.
  useEffect(() => {
    if (!stored) return;
    applyDynamicMeta(
      resultsMeta({
        productLabel:
          stored.productLabel ??
          (PRODUCTS as Record<string, { label?: string }>)[product]?.label ??
          "Investor loan",
        firstName: stored.firstName,
        estimatedDscr: stored.results.estimatedDscr,
        maxLoanAmount: stored.results.maxLoanAmount,
        monthlyCashFlow: stored.results.monthlyCashFlow,
        programFit: stored.results.programFit,
      }),
    );
  }, [stored, product]);

  const appHref = getApplicationHref(settings);
  const bookHref = getBookingHref(settings);
  const appLinkProps = getCtaLinkProps(appHref);
  const bookLinkProps = getCtaLinkProps(bookHref);


  useEffect(() => {
    if (!storedLoaded || conversionFired.current) return;
    conversionFired.current = true;
    // Fire the GA4 / dataLayer conversion event once. Uses a unique URL per
    // product so GSC + GA can attribute conversions cleanly.
    const path =
      typeof window !== "undefined" ? window.location.pathname : `/apply/thanks/${product}`;
    const offer = loadOfferAttribution() ?? {};
    trackEvent("lead_form_confirmation", {
      ...offer,
      product,
      lead_id: search.leadId ?? null,
      page_path: path,
    });
    trackEvent("lead_submitted", {
      ...offer,
      product,
      lead_id: search.leadId ?? null,
      state: stored?.state ?? null,
      city: stored?.city ?? null,
      goal: stored?.goal ?? null,
      page_path: path,
    });
    void import("@/lib/tracking").then((m) =>
      m.trackThankYouView({
        product,
        productLabel: (PRODUCTS as Record<string, { label?: string }>)[product]?.label,
        leadId: search.leadId,
        state: stored?.state ?? undefined,
        city: stored?.city ?? undefined,
        goal: stored?.goal ?? undefined,
        path,
      }),
    );
  }, [product, search.leadId, stored, storedLoaded]);

  return (
    <SiteShell>
      <PageHero
        eyebrow="Application received"
        title={copy.headline}
        subtitle={copy.subhead}
      />
      <Section>
        <div className="mx-auto max-w-5xl space-y-8">
          <div
            role="status"
            className="flex flex-col items-center gap-2 rounded-2xl border border-brand-gold/40 bg-brand-gold/10 px-4 py-4 text-center sm:flex-row sm:items-center sm:gap-3 sm:px-6 sm:text-left"
          >
            <CheckCircle2 aria-hidden="true" className="h-6 w-6 shrink-0 text-brand" />
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">
                Your file is queued. A licensed loan officer will reach out within
                one business day with pricing feedback.
              </p>
              {search.leadId && (
                <p className="mt-1 break-all text-[11px] text-muted-foreground">
                  Reference ID: <span className="font-mono">{search.leadId}</span>
                </p>
              )}
            </div>
          </div>


          {stored?.qualification && (
            <div>
              <h2 className="mb-2 text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                Your pre-eligibility check
              </h2>
              <PreQualResultCard
                qual={stored.qualification}
                product={
                  stored.productLabel ??
                  (PRODUCTS as Record<string, { label?: string }>)[product]?.label ??
                  "Investor loan"
                }
              />
            </div>
          )}

          {stored && (
            <PersonalizedResultsPanel
              results={stored.results}
              product={product}
              productLabel={stored.productLabel}
              documents={copy.documents}
              nextSteps={copy.nextSteps}
              timeline={copy.timeline}
              firstName={stored.firstName}
              leadId={search.leadId}
              compact
            />

          )}


          {/* Here's what happens next  +  Skip the wait */}
          <div className="grid items-start gap-8 lg:grid-cols-2">
            <div>
              <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                {settings.thanks_next_heading}
              </h2>
              <ul className="mt-4 space-y-4">
                <li className="flex gap-3">
                  <Mail aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-base text-foreground">
                    {settings.thanks_next_email_text}
                  </span>
                </li>
                <li className="flex gap-3">
                  <PhoneCall aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-base text-foreground">
                    {settings.thanks_next_phone_text}
                  </span>
                </li>
                <li className="flex gap-3">
                  <MessageSquare aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-base text-foreground">
                    {settings.thanks_next_confirmation_text}
                  </span>
                </li>
                <li className="flex gap-3">
                  <Clock aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-base text-foreground">
                    {copy.timeline} If it's after hours, pick a time below that works for you.
                  </span>
                </li>
              </ul>

              <div className="mt-4 rounded-2xl border border-border bg-card p-5">
                <p className="text-sm text-muted-foreground">
                  {settings.thanks_next_email_fallback_text}
                </p>
                <a
                  href={appHref}
                  {...appLinkProps}
                  onClick={() => ctaClick("complete_application", "next_steps_fallback", appHref)}
                  className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-gold px-5 py-3 text-sm font-black text-primary transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {settings.thanks_apply_button}
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </a>
              </div>

              <div className="mt-6 rounded-2xl border border-border bg-card p-5">
                <p className="text-sm font-semibold text-foreground">
                  {settings.thanks_callback_prompt}
                </p>
                <a
                  href={bookHref}
                  {...bookLinkProps}
                  onClick={() => ctaClick("schedule_callback", "next_steps_callback", bookHref)}
                  className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <CalendarClock aria-hidden="true" className="h-4 w-4" />
                  {settings.thanks_callback_button}
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-muted/40 p-6 md:p-8">
              <h2 className="font-display text-2xl font-extrabold leading-tight text-foreground md:text-3xl">
                {settings.thanks_skip_heading}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                {settings.thanks_skip_body}
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {settings.thanks_skip_bullets.map((t) => (
                  <div
                    key={t}
                    className="flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground"
                  >
                    <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-brand" />
                    {t}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col gap-3 pb-16 sm:flex-row sm:items-center sm:pb-0">
                <a
                  href={appHref}
                  {...appLinkProps}
                  onClick={() => ctaClick("complete_application", "skip_the_wait", appHref)}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-gold px-5 py-3 text-center text-sm font-black sm:w-auto text-primary transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {settings.thanks_apply_button}
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </a>
                <span className="text-xs text-muted-foreground">256-bit encrypted &amp; secure</span>
              </div>
            </div>
          </div>

          {hasSnapshotData(snapshot) && snapshot && (
            <PropertySnapshotCard snapshot={snapshot} />
          )}

          {product === "str" && search.leadId && (
            <StrReportPanel leadId={search.leadId} />
          )}




          <div className="grid gap-6">


            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-[11px] font-black uppercase tracking-widest text-brand">
                Documents to gather
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                We'll send a secure upload link — no need to email files.
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {copy.documents.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <DocumentChecklistDownload
            productLabel={
              (PRODUCTS as Record<string, { label?: string }>)[product]?.label ??
              "Investor loan"
            }
            documents={copy.documents}
            nextSteps={copy.nextSteps}
            timeline={copy.timeline}
            leadId={search.leadId}
          />

          <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
            <p>
              Prefer to talk now? Call{" "}
              <a href={telHref(settings.company_phone)} aria-label={`Call Simply Approved Mortgages at ${settings.company_phone}`} className="rounded-sm font-bold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                {settings.company_phone}
              </a>{" "}
              or{" "}
              <Link to="/contact" className="rounded-sm font-bold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                send us a message
              </Link>
              .
            </p>

              <p className="mt-3">
                <a
                  href={appHref}
                  {...appLinkProps}
                  onClick={() => ctaClick("complete_application", "footer_link", appHref)}
                  className="rounded-sm font-bold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  Continue to your full application →
                </a>
              </p>

            <p className="mt-3">
              <Link to="/" className="rounded-sm font-bold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                ← Back to home
              </Link>
            </p>
          </div>
        </div>
      </Section>
    </SiteShell>
  );
}
